import { Users, Wrench, Building2, Briefcase, FolderKanban, FileText, Store, type LucideIcon } from "lucide-react";
import { CATEGORY_LABELS } from "../../data/postTaxonomy";

import { services as mockServices, companies as mockCompanies, jobs as mockJobs, projects as mockProjects, rfqs as mockRfqs } from "../../data/mock";
import { api } from "../../lib/api";

import { postHref } from "../../lib/postNavigation";

export const TABS = ["Services", "Marketplace", "Jobseekers", "Companies", "Jobs", "Project Bidding", "RFQs"];

export const normalizeTab = (value: string | null) => {
  if (value === "Materials") return "Marketplace";
  if (value === "People" || value === "Workers") return "Jobseekers";
  if (value === "Projects") return "Project Bidding";
  return value;
};

export const KIND: Record<string, "people" | "services" | "companies" | "jobs" | "projects" | "rfqs"> = {
  Jobseekers: "people", Services: "services", Companies: "companies", Jobs: "jobs", "Project Bidding": "projects", RFQs: "rfqs",
};

export const mapService = (s: any) => ({ ...s, from: s.fromPrice ?? s.from ?? 0 });

export const MOCK: Record<string, any[]> = {
  Jobseekers: [], Services: mockServices.map(mapService), Companies: mockCompanies, Jobs: mockJobs, "Project Bidding": mockProjects, RFQs: mockRfqs, Marketplace: [],
};

export const emptyFilters = { q: "", city: "", category: "", categories: [] as string[], itemTypes: [] as string[], profession: "", minRating: 0, verified: false, sort: "newest", orgTypes: [] as string[] };

export const LOAD_MORE_ANIMATION_MIN_MS = 2000;

export const hasRating = (tab: string) => tab === "Jobseekers" || tab === "Services" || tab === "Companies";

export const hasRatingFilter = (tab: string) => tab === "Jobseekers" || tab === "Companies" || tab === "Marketplace";

export const hasPriceSort = (tab: string) => tab === "Services" || tab === "Marketplace";

export function fetchDiscoveryPage(tab: string, filters: typeof emptyFilters, cursor?: string, signal?: AbortSignal) {
  const commonParams = {
    q: filters.q || undefined,
    city: filters.city || undefined,
    sort: filters.sort || undefined,
    cursor,
    limit: 10,
  };
  if (tab === "Marketplace") return api.listItemsPage({
    ...commonParams,
    itemTypes: filters.itemTypes,
    minRating: filters.minRating || undefined,
  }, signal);
  return api.listPage(KIND[tab], {
    ...commonParams,
    category: tab !== "Companies" ? filters.category || undefined : undefined,
    categories: tab !== "Companies" && tab !== "Jobseekers" ? filters.categories : undefined,
    profession: tab === "Jobseekers" ? filters.profession || undefined : undefined,
    minRating: hasRatingFilter(tab) ? filters.minRating || undefined : undefined,
    verified: hasRating(tab) ? filters.verified || undefined : undefined,
    orgTypes: tab === "Companies" ? filters.orgTypes : undefined,
  }, signal);
}

export function filtersFromUrl(params: URLSearchParams, tab: string): Partial<typeof emptyFilters> {
  const filters: Partial<typeof emptyFilters> = {};
  for (const key of ["q", "city"] as const) if (params.has(key)) filters[key] = params.get(key) || "";
  const selections = (plural: string, legacy: string) => [...new Set(params.getAll(params.has(plural) ? plural : legacy).map((value) => value.trim()).filter(Boolean))];
  if (tab === "Marketplace" && (params.has("itemTypes") || params.has("itemType"))) filters.itemTypes = selections("itemTypes", "itemType");
  if (tab === "Companies" && (params.has("orgTypes") || params.has("orgType"))) filters.orgTypes = selections("orgTypes", "orgType");
  if (tab !== "Marketplace" && tab !== "Companies") {
    if (tab !== "Jobseekers" && params.has("categories")) filters.categories = selections("categories", "category");
    else if (params.has("category")) {
      const category = params.get("category") || "";
      // Preserve older free-text/landing-page filters; canonical category URLs use the new selection list.
      if (tab !== "Jobseekers" && CATEGORY_LABELS.includes(category)) filters.categories = [category];
      else filters.category = category;
    }
  }
  if (tab === "Jobseekers" && params.has("profession")) filters.profession = params.get("profession") || "";
  if (hasRating(tab)) {
    if (params.has("verified")) filters.verified = params.get("verified") === "true";
  }
  if (hasRatingFilter(tab) && params.has("minRating")) {
    const rating = Number(params.get("minRating"));
    filters.minRating = Number.isFinite(rating) && rating >= 0 && rating <= 5 ? rating : 0;
  }
  if (params.has("sort")) {
    const sort = params.get("sort") || "";
    filters.sort = sort === "newest" || (sort === "rating" && hasRating(tab)) || (sort === "price" && hasPriceSort(tab)) ? sort : "newest";
  }
  return filters;
}

export type ApplicationStatusesState = "loading" | "ready" | "error";

export const DIRECTORY_GROUPS: { key: string; label: string; Icon: LucideIcon; tabs: string[] }[] = [
  { key: "providers", label: "Explore Providers", Icon: Users, tabs: ["Services", "Jobseekers", "Companies"] },
  { key: "opportunities", label: "Find Opportunities", Icon: Briefcase, tabs: ["Jobs", "Project Bidding", "RFQs"] },
  { key: "marketplace", label: "Marketplace", Icon: Store, tabs: ["Marketplace"] },
];

export const TAB_PRESENTATION: Record<string, { label: string; intent: string; Icon: LucideIcon }> = {
  Services: { label: "Services", intent: "Find the service you need.", Icon: Wrench },
  Jobseekers: { label: "Jobseekers", intent: "Find people to hire.", Icon: Users },
  Companies: { label: "Companies", intent: "Find companies for larger or specialized work.", Icon: Building2 },
  Jobs: { label: "Jobs", intent: "Find a job that matches your skills.", Icon: Briefcase },
  "Project Bidding": { label: "Project Bidding", intent: "Find projects and submit your proposal.", Icon: FolderKanban },
  RFQs: { label: "RFQs", intent: "Find service or supply requests you can fulfill and submit your quotation.", Icon: FileText },
  Marketplace: { label: "Materials, Products & Equipment", intent: "Shop for materials, products, and equipment for your work.", Icon: Store },
};

export const SURFACE_META: Record<string, { route: string; title: string; subtitle: string; Icon: LucideIcon; cta: { label: string; href: string } }> = {
  Jobseekers: { route: "/jobseekers", title: "Jobseekers", subtitle: TAB_PRESENTATION.Jobseekers.intent, Icon: Users, cta: { label: "Set up your Jobseeker profile", href: "/worker-profile" } },
  Services: { route: "/services", title: "Services", subtitle: TAB_PRESENTATION.Services.intent, Icon: Wrench, cta: { label: "Offer a service", href: postHref("service", "/services") } },
  Companies: { route: "/companies", title: "Companies", subtitle: TAB_PRESENTATION.Companies.intent, Icon: Building2, cta: { label: "Add your company", href: postHref("company", "/companies") } },
  Jobs: { route: "/jobs", title: "Jobs", subtitle: TAB_PRESENTATION.Jobs.intent, Icon: Briefcase, cta: { label: "Post a job", href: postHref("job", "/jobs") } },
  "Project Bidding": { route: "/projects", title: "Project Bidding", subtitle: TAB_PRESENTATION["Project Bidding"].intent, Icon: FolderKanban, cta: { label: "Post a project", href: postHref("project", "/projects") } },
};
